import React from 'react';
import { EducationFormatter } from '@/utils/educationFormatter';

interface ResumeData {
  personalInfo: {
    name: string;
    title: string;
    email: string;
    phone: string;
    location: string;
    linkedin?: string;
    website?: string;
  };
  summary: string;
  experience: Array<{
    title: string;
    company: string;
    location: string;
    dates: string;
    bullets: string[];
  }>;
  education: Array<{
    degree: string;
    school: string;
    location: string;
    dates: string;
    gpa?: string;
  }>;
  skills: string[];
}

interface Template5Props {
  data: ResumeData;
}

export const Template5: React.FC<Template5Props> = ({ data }) => {
  return (
    <div className="max-w-4xl mx-auto bg-white text-gray-900 font-sans">
      {/* Minimal Header */}
      <header className="py-6 mb-8">
        <h1 className="text-5xl font-thin text-gray-900 mb-1">{data.personalInfo.name}</h1>
        <div className="w-16 h-1 bg-orange-500 mb-4"></div>
        <h2 className="text-lg text-gray-600 font-light mb-6">{data.personalInfo.title}</h2>
        
        <div className="grid grid-cols-2 gap-8 text-sm">
          <div className="space-y-1">
            <p className="text-gray-700">{data.personalInfo.email}</p>
            <p className="text-gray-700">{data.personalInfo.phone}</p>
          </div>
          <div className="space-y-1">
            <p className="text-gray-700">{data.personalInfo.location}</p>
            {data.personalInfo.linkedin && <p className="text-gray-700">{data.personalInfo.linkedin}</p>}
            {data.personalInfo.website && <p className="text-gray-700">{data.personalInfo.website}</p>}
          </div>
        </div>
      </header>

      {/* Summary */}
      <section className="mb-10">
        <h3 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wider">
          Profile
        </h3>
        <p className="text-gray-700 leading-relaxed text-lg font-light">{data.summary}</p>
      </section>

      {/* Experience */}
      <section className="mb-10">
        <h3 className="text-sm font-bold text-gray-900 mb-6 uppercase tracking-wider">
          Experience
        </h3>
        
        {data.experience.map((job, index) => (
          <div key={index} className="mb-8 pb-8 border-b border-gray-200 last:border-b-0">
            <div className="grid grid-cols-3 gap-8 mb-4">
              <div className="col-span-2">
                <h4 className="text-xl font-light text-gray-900 mb-1">{job.title}</h4>
                <p className="text-orange-600 font-medium">{job.company}</p>
                <p className="text-sm text-gray-600">{job.location}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600 font-medium">{job.dates}</p>
              </div>
            </div>
            
            <ul className="space-y-3 text-gray-700">
              {job.bullets.map((bullet, bulletIndex) => (
                <li key={bulletIndex} className="flex items-start">
                  <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2 mr-4 flex-shrink-0"></span>
                  <span className="font-light">{bullet}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </section>

      {/* Education and Skills */}
      <div className="grid grid-cols-2 gap-12">
        {/* Education */}
        <section>
          <h3 className="text-sm font-bold text-gray-900 mb-6 uppercase tracking-wider">
            Education
          </h3>
          
          <EducationFormatter 
            education={data.education} 
            className="text-gray-900"
          />
        </section>

        {/* Skills */}
        <section>
          <h3 className="text-sm font-bold text-gray-900 mb-6 uppercase tracking-wider">
            Skills
          </h3>
          
          <div className="space-y-3">
            {data.skills.map((skill, index) => (
              <div key={index} className="text-gray-700 font-light">
                {skill}
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};